import React, { createContext, useEffect, useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'
import {
  BrandConfig,
  BrandKey,
  detectBrandFromHostname,
  getBrandConfig,
  getBrandHomePath,
  getBrandPath
} from '../config/brands.config'
import { useBrandDocumentMeta } from '../hooks/useBrandDocumentMeta'

interface BrandContextData {
  brand: BrandKey
  config: BrandConfig
  setBrand: (brand: BrandKey) => void
  /** Marca whitelabel (qualquer marca que não seja a Cannect). */
  isBranded: boolean
  /** Home do fluxo da marca: '/' na Cannect, agendamento da marca nas demais. */
  homePath: string
  /** Prefixa o path com o da marca ('/consulta-na-hora' → '/vikura/consulta-na-hora'). */
  brandPath: (subpath: string) => string
}

export const BrandContext = createContext<BrandContextData>({} as BrandContextData)

interface BrandProviderProps {
  children: React.ReactNode
}

export function BrandProvider({ children }: BrandProviderProps) {
  const detectedBrand = useMemo(() => {
    if (typeof window !== 'undefined') {
      return detectBrandFromHostname(window.location.hostname, window.location.pathname)
    }
    return 'cannect' as BrandKey
  }, [])

  const [brand, setBrand] = useState<BrandKey>(detectedBrand)

  const { pathname } = useLocation()

  useEffect(() => {
    const newBrand = detectBrandFromHostname(window.location.hostname, pathname)
    if (newBrand !== brand) {
      setBrand(newBrand)
    }
  }, [pathname, brand])

  const config = useMemo(() => getBrandConfig(brand), [brand])

  useEffect(() => {
    const root = document.documentElement

    root.style.setProperty('--brand-primary', config.colors.primary)
    root.style.setProperty('--brand-secondary', config.colors.secondary)
    root.style.setProperty('--brand-accent', config.colors.accent)
    root.style.setProperty('--brand-background', config.colors.background)
    root.style.setProperty('--brand-text', config.colors.text)
    root.style.setProperty('--brand-error', config.colors.error)
    root.style.setProperty('--brand-success', config.colors.success)
  }, [config])

  useBrandDocumentMeta(config)

  const value = useMemo(
    () => ({
      brand,
      config,
      setBrand,
      isBranded: brand !== 'cannect',
      homePath: getBrandHomePath(config),
      brandPath: (subpath: string) => getBrandPath(config, subpath)
    }),
    [brand, config]
  )

  return <BrandContext.Provider value={value}>{children}</BrandContext.Provider>
}
